import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import { useId } from '../hooks/UNSAFE_useId';
import { DirectionalCollapseArrowIcon } from '../PRIVATE_ThemedIcons/DirectionalCollapseArrowIcon';
import { DirectionalExpandArrowIcon } from '../PRIVATE_ThemedIcons/DirectionalExpandArrowIcon';

import { BaseMenuItem } from './MenuItem';
import { SheetMenu } from './SheetMenu';
import { useMenuContainerContext } from './useMenuContainerContext';
import { SubmenuProps } from './Submenu';

export const SheetSubmenu = ({ label, children, startIcon }: SubmenuProps) => {
  const submenuId = useId();
  const submenuItemId = useId();
  const submenuTitleId = useId();

  const submenuMenuItemRef = useRef<HTMLAnchorElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);

  const [initialFocus, setInitialFocus] = useState<'menu' | 'secondItem'>('menu');

  const { menuContainerRef, changeKey } = useMenuContainerContext();
  /* We have to wrap opening display change inside a useEffect to avoid flashy effect when opening submenu*/
  useEffect(() => {
    if (isOpen) {
      if (menuContainerRef.current) {
        /*Menu hidden instead of unmounted for two main reasons:
      1. Children need to be updated even if menu is present.
      2. We can't unmount parent since all the submenus are children of main menuContainer*/
        menuContainerRef.current.style.display = 'none';
      }
    }
  }, [isOpen, menuContainerRef]);

  const openSubmenuDirectional = useCallback(() => {
    setIsOpen(true);
    setInitialFocus('secondItem');
  }, []);

  const openSubmenuAction = useCallback((e: Event) => {
    //If the submenu is open using keyboard focus has to go to second item(first is the title), if not to the menu Container
    setIsOpen(true);
    if (e.type === 'keyup') {
      setInitialFocus('secondItem');
    } else {
      setInitialFocus('menu');
    }
  }, []);

  const returnFocusToSubmenuItem = useCallback(() => {
    changeKey({ key: submenuItemId, reason: 'keyboard' });
    submenuMenuItemRef.current?.focus();
  }, [changeKey, submenuItemId, submenuMenuItemRef]);

  /* No need to "save" these functions with useCallback */
  const closeSubmenu = () => {
    setIsOpen(false);
    if (menuContainerRef.current) {
      //Submenu is unmounted and prev menu is visible again.
      /*Dom manipulation is kind of dirty. Have to check for new approaches. JET-57239 */
      menuContainerRef.current.style.display = 'block';
    }
  };

  const closeSubmenuPressingTitleItem = (e: Event) => {
    closeSubmenu();
    //If the submenu is closed using keyboard focus has to go to submenuItem that opened submenu, if not to the menu Container
    if (e.type === 'keyup') {
      returnFocusToSubmenuItem();
    } else {
      changeKey({ key: undefined, reason: 'click' });
      menuContainerRef.current?.focus();
    }
  };

  const closeDirectional = () => {
    closeSubmenu();
    returnFocusToSubmenuItem();
  };

  return (
    <>
      <BaseMenuItem
        startIcon={startIcon}
        id={submenuItemId}
        submenuId={submenuId}
        ref={submenuMenuItemRef}
        label={label}
        endIcon={<DirectionalExpandArrowIcon></DirectionalExpandArrowIcon>}
        type="submenu"
        role="menuitem"
        onAction={openSubmenuAction}
        openSubmenu={openSubmenuDirectional}
        isSubmenuOpen={isOpen}></BaseMenuItem>
      {!isOpen ? null : (
        <SheetMenu
          menuId={submenuId}
          closeSubmenu={closeDirectional}
          menuLevel="sub"
          anchorRef={submenuMenuItemRef}
          //TODO: We need to check if this initial focus is desired based on some W3C specs
          currentFocus={initialFocus}>
          <BaseMenuItem
            id={submenuTitleId}
            menuItemLabelWeight="bold"
            role="menuitem"
            type="submenu"
            startIcon={<DirectionalCollapseArrowIcon></DirectionalCollapseArrowIcon>}
            onAction={closeSubmenuPressingTitleItem}
            label={label}></BaseMenuItem>
          {children}
        </SheetMenu>
      )}
    </>
  );
};
